import axios from 'src/utils/axios';
import helpers from 'src/helpers';
import { NOTIFICATION_STATUS, PERMISSIONS } from 'src/constants';
import { getAccountInfo } from 'src/actions/accountActions';
import { addNotification } from 'src/actions/notificationActions';

export const SET_GROUPS = '@groups/set-groups';
export const GET_GROUPS = '@groups/get-groups';
export const GET_GROUPS_SUCCESS = '@groups/get-groups-success';
export const GET_GROUPS_ERROR = '@groups/get-groups-error';

export const GET_GROUP_TYPES = '@groups/get-group-types';
export const GET_GROUP_TYPES_SUCCESS = '@groups/get-group-types-success';
export const GET_GROUP_TYPES_ERROR = '@groups/get-group-types-error';

export const GET_FOLLOW_UP_MESSAGES = '@groups/get-follow-up-messages';
export const GET_FOLLOW_UP_MESSAGES_SUCCESS = '@groups/get-follow-up-messages-success';
export const GET_FOLLOW_UP_MESSAGES_ERROR = '@groups/get-follow-up-messages-error';

export const SET_GROUPS_QUERY = '@groups/set-groups-query';
export const CLEAR_GROUPS_QUERY_FILTER = '@groups/clear-groups-query-filter';

export const SET_SELECTED_GROUPS = '@groups/set-selected-groups';

export const SET_VISIBLE_MODAL_FORM_NUMBER = '@groups/set-visible-modal-form-number';

export const SET_VALUES_FORM_SEND_TEXT = '@groups/set-values-form-send-text';

export const SEND_TEXT_MESSAGE = '@groups/send-text-message';
export const SEND_TEXT_MESSAGE_SUCCESS = '@groups/send-text-message-success';
export const SEND_TEXT_MESSAGE_ERROR = '@groups/send-text-message-error';

export const SEND_ADD_GROUP = '@groups/send-add-group';
export const SEND_ADD_GROUP_SUCCESS = '@groups/send-add-group-success';
export const SEND_ADD_GROUP_ERROR = '@groups/send-add-group-error';

export const SEND_ADD_CATEGORY = '@groups/send-add-category';
export const SEND_ADD_CATEGORY_SUCCESS = '@groups/send-add-category-success';
export const SEND_ADD_CATEGORY_ERROR = '@groups/send-add-category-error';

export const SET_VALUES_FORM_ADD_CATEGORY = '@groups/set-values-form-add-category';

export const REPLACE_SYSTEM_GEN_MESSAGE = '@groups/replace-system-gen-message';
export const REPLACE_SYSTEM_GEN_MESSAGE_SUCCESS = '@groups/replace-system-gen-message-success';
export const REPLACE_SYSTEM_GEN_MESSAGE_ERROR = '@groups/replace-system-gen-message-error';

export const SET_BACK_TO_GROUP_LIST = '@groups/set-back-to-group-list';

export const SEND_INACTIVATE_GROUPS = '@groups/send-inactivate-groups';
export const SEND_INACTIVATE_GROUPS_SUCCESS = '@groups/send-inactivate-groups-success';
export const SEND_INACTIVATE_GROUPS_ERROR = '@groups/send-inactivate-groups-error';

export const getGroups = () => async (dispatch, getState) => {
  dispatch({
    type: GET_GROUPS,
  });
  try {
    // CON-2026 — activity badges (P1d): the "Unseen (N)" filter rides on
    // query.newOnly, sent only by ACTIVITY_BADGES holders.
    const { newOnly, ...restOfQuery } = getState()?.groups?.query || {};
    const hasActivityBadges = helpers.permissions.doesUserHavePermission(
      PERMISSIONS.PERMISSIONS_SYSTEM_CODE.ACTIVITY_BADGES,
      getState()?.account?.user?.userRole?.permissions || [],
    );
    const body = hasActivityBadges && newOnly ? { ...restOfQuery, newOnly: true } : restOfQuery;
    const response = await axios.post('/api/groups', body);
    dispatch({
      type: GET_GROUPS_SUCCESS,
      payload: response?.data || {}
    });
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(GET_GROUPS_ERROR, helpers.transform.toString(message || error || 'Server error'), NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: GET_GROUPS_ERROR,
    });
  }
};

// CON-1850 — every group (active and inactive, never deleted) for a picker.
// Deliberately NOT getGroups(): that posts the Groups list page's own query,
// which is paginated (50 per page, active only), so a picker built from it
// silently missed every group past the first page. Returns the array to the
// caller and leaves the Groups list page's Redux state untouched.
export const getAllGroupsForPicker = () => async (dispatch) => {
  try {
    const response = await axios.get('/api/groups/list', { params: { includeInactive: true } });
    return response?.data?.groups || [];
  } catch (e) {
    const {
      error,
      message,
    } = e?.response?.data || {};
    dispatch(addNotification(GET_GROUPS_ERROR, helpers.transform.toString(message || error || 'Server error'), NOTIFICATION_STATUS.ERROR));
    return [];
  }
};

// CON-1552 Groups — same-user, member-free Duplicate ("<base> - Copy").
// doWhenTry receives the new group's id so the caller can route to it.
export const duplicateGroup = (
  id,
  doWhenTry = false,
  doWhenCatch = false
) => async (dispatch) => {
  try {
    const response = await axios.post(`/api/groups/${id}/duplicate`);
    const { group } = response?.data || {};

    if (!group?.id) {
      dispatch(addNotification('DUPLICATE_GROUP_ERROR', 'Cannot duplicate group', NOTIFICATION_STATUS.ERROR));
      if (doWhenCatch) doWhenCatch();
      return;
    }

    dispatch(addNotification('DUPLICATE_GROUP_SUCCESS', `Group "${group.groupName}" has been successfully created`, NOTIFICATION_STATUS.SUCCESS));

    if (doWhenTry) doWhenTry(group.id);
  } catch (e) {
    const { error, message } = e?.response?.data || {};
    dispatch(addNotification('DUPLICATE_GROUP_ERROR', helpers.transform.toString(message || error || 'Server error'), NOTIFICATION_STATUS.ERROR));
    if (doWhenCatch) doWhenCatch(e);
  }
};

export const getGroupTypes = () => async (dispatch) => {
  try {
    dispatch({
      type: GET_GROUP_TYPES,
    });
    const { data } = await axios.post('/api/groupSettings/grouptypes', { status: 'Active' });
    const { error } = data || {};

    if (error) {
      dispatch({
        type: GET_GROUP_TYPES_ERROR,
      });
      return;
    }

    dispatch({
      type: GET_GROUP_TYPES_SUCCESS,
      payload: data?.groupTypes || {},
    });
  } catch (e) {
    dispatch({
      type: GET_GROUP_TYPES_ERROR,
    });
  }
};

export const setGroups = (groups) => ({
  type: SET_GROUPS,
  payload: groups
});

export const clearGroupTypes = () => ({
  type: GET_GROUP_TYPES_SUCCESS,
});

export const setGroupsQuery = (query) => ({
  type: SET_GROUPS_QUERY,
  payload: query
});

export const clearGroupsQueryFilter = () => ({
  type: CLEAR_GROUPS_QUERY_FILTER,
});

export const setSelectedGroups = (selectedGroups) => ({
  type: SET_SELECTED_GROUPS,
  payload: selectedGroups
});

export const setVisibleModalFormNumber = (modalNumber) => ({
  type: SET_VISIBLE_MODAL_FORM_NUMBER,
  payload: modalNumber
});

export const setValuesFormSendText = (values) => ({
  type: SET_VALUES_FORM_SEND_TEXT,
  payload: values
});

// export const getPurchasedPartyOptions = (type) => async (dispatch) => {
//   dispatch({
//     type: GET_PURCHASED_PARTY_OPTIONS,
//   });
//   try {
//     const response = await axios.post('/api/groups/party-filter-options', { type });
//     dispatch({
//       type: GET_PURCHASED_PARTY_OPTIONS_SUCCESS,
//       payload: response?.data || {}
//     });
//   } catch (e) {
//     const {
//       error,
//       message,
//     } = e?.response?.data || {};
//     dispatch(addNotification(GET_PURCHASED_PARTY_OPTIONS_ERROR, helpers.transform.toString(message || error || 'Server error'), NOTIFICATION_STATUS.ERROR));
//     dispatch({
//       type: GET_PURCHASED_PARTY_OPTIONS_ERROR,
//     });
//   }
// };

// export const sendTextMessage = (sendMessage, doWhenTry = false, doWhenCatch = false) => async (dispatch) => {
//   try {
//     dispatch({
//       type: SEND_TEXT_MESSAGE,
//     });
//     const response = await axios.put('/api/contacts/sendmessage', { sendMessage });
//     const { success } = response?.data || {};

//     if (!success) {
//       dispatch(addNotification(SEND_TEXT_MESSAGE_ERROR, 'Try again later', NOTIFICATION_STATUS.ERROR));
//       dispatch({
//         type: SEND_TEXT_MESSAGE_ERROR,
//       });
//       return;
//     }

//     dispatch(addNotification(SEND_TEXT_MESSAGE_SUCCESS, 'Your text has been scheduled to send.', NOTIFICATION_STATUS.SUCCESS));
//     dispatch({
//       type: SEND_TEXT_MESSAGE_SUCCESS,
//     });

//     if (doWhenTry) {
//       doWhenTry();
//     }
//   } catch (e) {
//     const {
//       message,
//       error,
//     } = e?.response?.data || {};
//     const { message: errorMessage } = error || {};
//     dispatch(addNotification(SEND_TEXT_MESSAGE_ERROR, message || errorMessage || 'Try again later', NOTIFICATION_STATUS.ERROR));
//     dispatch({
//       type: SEND_TEXT_MESSAGE_ERROR,
//     });

//     if (doWhenCatch) {
//       doWhenCatch(e);
//     }
//   }
// };

export const setValuesFormAddGroup = (values) => ({
  type: SEND_ADD_GROUP,
  payload: values
});

export const sendAddGroup = () => {

};

export const setValuesFormAddCategory = (values) => ({
  type: SET_VALUES_FORM_ADD_CATEGORY,
  payload: values
});

export const sendAddCategory = (addTagCampaign, doWhenTry = false, doWhenCatch = false) => async (dispatch) => {
  try {
    dispatch({
      type: SEND_ADD_CATEGORY,
    });
    const response = await axios.put('/api/contacts/addtagcampaign', { addTagCampaign });
    const { success } = response?.data || {};

    if (!success) {
      dispatch(addNotification(SEND_ADD_CATEGORY_ERROR, 'Try again later', NOTIFICATION_STATUS.ERROR));
      dispatch({
        type: SEND_ADD_CATEGORY_ERROR,
      });
      return;
    }

    dispatch(addNotification(SEND_ADD_CATEGORY_SUCCESS, 'The category has been added.', NOTIFICATION_STATUS.SUCCESS));
    dispatch({
      type: SEND_ADD_CATEGORY_SUCCESS,
    });

    if (doWhenTry) {
      doWhenTry();
    }
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    const { message: errorMessage } = error || {};
    dispatch(addNotification(SEND_ADD_CATEGORY_ERROR, message || errorMessage || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: SEND_ADD_CATEGORY_ERROR,
    });

    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};

// export const getFollowUpMessages = () => async (dispatch) => {
//   try {
//     dispatch({
//       type: GET_FOLLOW_UP_MESSAGES,
//     });
//     const { data } = await axios.post('/api/followupmessages/template-tag') || {};
//     const { error } = data || {};

//     if (error) {
//       dispatch({
//         type: GET_FOLLOW_UP_MESSAGES_ERROR,
//       });
//       return;
//     }

//     dispatch({
//       type: GET_FOLLOW_UP_MESSAGES_SUCCESS,
//       payload: data?.messages,
//     });
//   } catch (e) {
//     dispatch({
//       type: GET_FOLLOW_UP_MESSAGES_ERROR,
//     });
//   }
// };

// export const clearFollowUpMessages = () => ({
//   type: GET_FOLLOW_UP_MESSAGES_SUCCESS,
// });

// export const replaceSystemGenMessage = (systemGenMessage, hostFirstName, distShort, boughtFromPartyName, calledBy = 'retrieveMessageToSend', lastJoinedPartyName, partyURL, lastJoinedPartyHostFirstName, lastJoinedPartyHostLastName, lastOrderDateTime, emailAddress) => async (dispatch) => {
//   try {
//     dispatch({
//       type: REPLACE_SYSTEM_GEN_MESSAGE,
//     });
//     const { data } = await axios.post('/api/contacts/replace-system-gen-message', {
//       systemGenMessage,
//       hostFirstName,
//       distShort,
//       boughtFromPartyName,
//       calledBy,
//       lastJoinedPartyName,
//       partyURL,
//       lastJoinedPartyHostFirstName,
//       lastJoinedPartyHostLastName,
//       lastOrderDateTime,
//       emailAddress
//     }) || {};

//     dispatch({
//       type: REPLACE_SYSTEM_GEN_MESSAGE_SUCCESS,
//       payload: data?.message,
//     });
//   } catch (e) {
//     dispatch({
//       type: REPLACE_SYSTEM_GEN_MESSAGE_ERROR,
//     });
//   }
// };

export const setBackToGroupList = (payload) => ({
  type: SET_BACK_TO_GROUP_LIST,
  payload,
});

export const clearBackToGroupList = (clearAll = false) => ({
  type: SET_BACK_TO_GROUP_LIST,
  payload: !clearAll ? {
    groups: [],
    query: {},
  } : false,
});

export const inactivateGroups = (selectedGroups, doWhenTry = false, doWhenCatch = false) => async (dispatch) => {
  try {
    dispatch({
      type: SEND_INACTIVATE_GROUPS,
    });
    const response = await axios.put('/api/groups/closeGroups', { selectedGroups });
    const { success } = response?.data || {};

    if (!success) {
      dispatch(addNotification(SEND_INACTIVATE_GROUPS_ERROR, 'Try again later', NOTIFICATION_STATUS.ERROR));
      dispatch({
        type: SEND_INACTIVATE_GROUPS_ERROR,
      });
      return;
    }

    const closeSuccessText = selectedGroups.length === 1 ? 'The group has been inactivated.' : 'The groups have been inactivated.';
    dispatch(addNotification(SEND_INACTIVATE_GROUPS_SUCCESS, closeSuccessText, NOTIFICATION_STATUS.SUCCESS));
    dispatch({
      type: SEND_INACTIVATE_GROUPS_SUCCESS,
    });
    dispatch(getAccountInfo());

    if (doWhenTry) {
      doWhenTry();
    }
  } catch (e) {
    const {
      message,
      error,
    } = e?.response?.data || {};
    const { message: errorMessage } = error || {};
    dispatch(addNotification(SEND_INACTIVATE_GROUPS_ERROR, message || errorMessage || 'Try again later', NOTIFICATION_STATUS.ERROR));
    dispatch({
      type: SEND_INACTIVATE_GROUPS_ERROR,
    });

    if (doWhenCatch) {
      doWhenCatch(e);
    }
  }
};
